import { shallow } from 'zustand/shallow';
import { SubstanceVerificationStatus } from '@/api/generated-types';
import { keyBy, indexBy } from '@/utils/data/collections';
import { DEFAULT_DECLARATION_PARAMETERS } from '@/types/validationFormTypes';
import type { ValidationResult } from '@/stores/validationStore';
import type {
  FormDangerousGoodsItem,
  ImdgSubstanceDetail,
  SubstanceVerificationResponse,
  SubstanceVerificationResult,
} from '@/api/types';
import type { ValidationFormData } from '@/types/validationFormTypes';

export function hasUsableVerificationResults(
  results: SubstanceVerificationResult[] | null | undefined
): boolean {
  return !!results?.some((r) => r.validationStatus !== SubstanceVerificationStatus.Error);
}

export function createEmptyValidationResult(cargoId?: string): ValidationResult {
  return {
    cargoId,
    validationStatus: undefined,
    substanceIds: [],
    selectedSubstanceId: null,
    selectedSubstance: undefined,
  };
}

/**
 * Maps API results to ValidationResults, enriching each from the deduplicated
 * enrichedSubstances dict. `formGoods` controls substanceId precedence: omitted
 * (verify) uses selectedSubstanceId ?? autoSelectedSubstanceId; provided
 * (curated/restore) lets formItem.substanceId win — the curator may have re-picked.
 */
export function convertValidationResults(
  results: SubstanceVerificationResult[],
  enrichedSubstances?: Record<string, ImdgSubstanceDetail> | null,
  formGoods?: FormDangerousGoodsItem[]
): ValidationResult[] {
  const formByCargoId = formGoods ? keyBy(formGoods, (g) => g.cargoId) : undefined;

  return results.map((r): ValidationResult => {
    const formItem = formByCargoId && r.cargoId ? formByCargoId.get(r.cargoId) : undefined;
    const substanceId = formItem?.substanceId ?? r.selectedSubstanceId ?? r.autoSelectedSubstanceId;
    return {
      ...r,
      cargoId: formItem?.cargoId ?? r.cargoId ?? undefined,
      selectedSubstanceId: substanceId ?? undefined,
      autoSelectedSubstanceId: r.autoSelectedSubstanceId ?? undefined,
      selectedSubstance: substanceId ? enrichedSubstances?.[substanceId] : undefined,
    };
  });
}

export function isSubstanceVerified(result: ValidationResult | undefined): boolean {
  if (!result) return false;
  return (
    result.validationStatus === SubstanceVerificationStatus.ExactMatch ||
    !!result.selectedSubstanceId
  );
}

/**
 * Cargo a Verify press should look up: no verdict yet, a verdict for a UN number the form no
 * longer holds, or a lookup that failed. NoMatch and MultipleMatches are answers, and asking
 * again gets the same one.
 */
export function cargoNeedingVerification(
  goods: readonly FormDangerousGoodsItem[],
  validationByCargoId: ReadonlyMap<string, ValidationResult>
): FormDangerousGoodsItem[] {
  return goods.filter((dg) => {
    const unNumber = dg.unNumber?.trim();
    if (!unNumber || !dg.cargoId) return false;
    const result = validationByCargoId.get(dg.cargoId);
    return (
      !result ||
      result.unNumber?.trim() !== unNumber ||
      result.validationStatus === SubstanceVerificationStatus.Error
    );
  });
}

/**
 * Upsert-merges new validation results into existing ones by cargoId.
 *
 * INVARIANT: form text fields (properShippingName, technicalName, hazardClass,
 * packingGroup) are user-owned — verification only links substanceId +
 * isMarinePollutant, never writes text. Display canonical text via selectedSubstance.
 */
export function mergeValidationResults(
  existing: ValidationResult[],
  newResults: ValidationResult[],
  allDangerousGoods: FormDangerousGoodsItem[]
): ValidationResult[] {
  const resultMap = keyBy(existing, (r) => r.cargoId);

  newResults.forEach((incoming) => {
    const cargoId = incoming.cargoId;
    if (!cargoId) return;

    const existingItem = resultMap.get(cargoId);

    // Preserve the user's substance selection on revalidation — but only while it still applies:
    // a re-extract that moved the UN number leaves the old pick describing different cargo.
    const carried =
      existingItem?.selectedSubstanceId &&
      (incoming.validationStatus !== SubstanceVerificationStatus.ExactMatch ||
        existingItem.unNumber === incoming.unNumber)
        ? existingItem
        : null;

    resultMap.set(
      cargoId,
      carried
        ? {
            ...incoming,
            cargoId,
            selectedSubstanceId: carried.selectedSubstanceId,
            selectedSubstance: carried.selectedSubstance,
            autoSelectedSubstanceId: carried.autoSelectedSubstanceId,
          }
        : { ...incoming, cargoId }
    );
  });

  allDangerousGoods.forEach((dg) => {
    if (dg.cargoId && !resultMap.has(dg.cargoId)) {
      resultMap.set(dg.cargoId, createEmptyValidationResult(dg.cargoId));
    }
  });

  return Array.from(resultMap.values());
}

export interface ValidationStatistics {
  exactMatches: number;
  multipleMatches: number;
  multipleMatchesWithSelection: number;
  noMatches: number;
  errors: number;
}

export function calculateValidationStatistics(
  validationResults: ValidationResult[],
  dangerousGoods: FormDangerousGoodsItem[]
): ValidationStatistics {
  let exactMatches = 0;
  let multipleMatches = 0;
  let multipleMatchesWithSelection = 0;
  let noMatches = 0;
  let errors = 0;

  const dgMap = keyBy(dangerousGoods, (dg) => dg.cargoId);

  validationResults.forEach((result) => {
    const cargoId = result.cargoId;
    if (!cargoId) return;
    const dgItem = dgMap.get(cargoId);
    if (!dgItem) return;

    if (result.validationStatus === SubstanceVerificationStatus.ExactMatch) {
      exactMatches++;
    } else if (result.validationStatus === SubstanceVerificationStatus.MultipleMatches) {
      if (dgItem.substanceId) {
        multipleMatchesWithSelection++;
      } else {
        multipleMatches++;
      }
    } else if (result.validationStatus === SubstanceVerificationStatus.NoMatch) {
      noMatches++;
    } else if (result.validationStatus === SubstanceVerificationStatus.Error) {
      errors++;
    }
  });

  return {
    exactMatches,
    multipleMatches,
    multipleMatchesWithSelection,
    noMatches,
    errors,
  };
}

export function applyValidationToForm(
  dangerousGoods: FormDangerousGoodsItem[],
  validationResults: ValidationResult[]
): {
  updatedGoods: FormDangerousGoodsItem[];
  /** Cargo whose form fields this write actually changed — empty when the form is already in step. */
  changedCargoIds: string[];
} & ValidationStatistics {
  const updatedGoods = [...dangerousGoods];
  const indexMap = indexBy(dangerousGoods, (dg) => dg.cargoId);
  const changedCargoIds: string[] = [];

  validationResults.forEach((result) => {
    const cargoId = result.cargoId;
    if (!cargoId) return;

    const targetIndex = indexMap.get(cargoId) ?? -1;
    if (targetIndex < 0) return;

    const before = updatedGoods[targetIndex];
    const isExactMatch = result.validationStatus === SubstanceVerificationStatus.ExactMatch;
    const hasSelection = !!result.selectedSubstanceId;

    if (isExactMatch || hasSelection) {
      const item = updatedGoods[targetIndex];
      const linkedSubstanceId = result.selectedSubstanceId ?? result.substanceIds?.[0] ?? '';
      const isMarinePollutant =
        result.selectedSubstance?.isMarinePollutant ||
        item.declarationParameters?.isMarinePollutant ||
        false;
      const substanceId = linkedSubstanceId || item.substanceId;
      const declarationParameters = {
        ...DEFAULT_DECLARATION_PARAMETERS,
        ...item.declarationParameters,
        isMarinePollutant,
      };
      // Rewriting an unchanged item would report every verified cargo as a form change.
      if (
        substanceId !== item.substanceId ||
        !shallow(declarationParameters, item.declarationParameters)
      ) {
        updatedGoods[targetIndex] = { ...item, substanceId, declarationParameters };
      }
    }

    const pv = result.packagingValidation;
    if (pv?.resolvedOuter && !updatedGoods[targetIndex].outerPackaging?.type) {
      const item = updatedGoods[targetIndex];
      updatedGoods[targetIndex] = {
        ...item,
        outerPackaging: {
          ...pv.resolvedOuter,
          count: item.outerPackaging?.count ?? pv.parsedOuterCount ?? undefined,
        },
        ...(pv.resolvedInner && {
          innerPackaging: {
            ...pv.resolvedInner,
            count: item.innerPackaging?.count ?? pv.parsedInnerCount ?? undefined,
          },
        }),
      };
    }

    if (updatedGoods[targetIndex] !== before) changedCargoIds.push(cargoId);
  });

  return {
    updatedGoods,
    changedCargoIds,
    ...calculateValidationStatistics(validationResults, dangerousGoods),
  };
}

/**
 * Cargo whose substance verdict moved, over the cargo this verify actually covered — the
 * merged set spans the whole document, so scanning it would cost the same for one row as
 * for three thousand. Their error report is stale even when no form field changed: a lookup
 * that timed out and then resolved leaves the form untouched but the verdict different.
 */
export function diffVerificationVerdicts(
  before: Map<string, ValidationResult>,
  after: Map<string, ValidationResult>,
  cargoIds: Iterable<string | null | undefined>
): string[] {
  const changed: string[] = [];
  for (const cargoId of cargoIds) {
    if (!cargoId) continue;
    const a = before.get(cargoId);
    const b = after.get(cargoId);
    if (
      a?.validationStatus !== b?.validationStatus ||
      (a?.selectedSubstanceId ?? undefined) !== (b?.selectedSubstanceId ?? undefined) ||
      (a?.error ?? undefined) !== (b?.error ?? undefined) ||
      // The slug, not just the message: the report words a substance issue from the slug, so a
      // move between slugs has to count even if whoever produced them reused the text.
      (a?.errorCode ?? undefined) !== (b?.errorCode ?? undefined)
    ) {
      changed.push(cargoId);
    }
  }
  return changed;
}

export interface ProcessRehydrationResult extends ValidationStatistics {
  mergedResults: ValidationResult[];
  updatedGoods: FormDangerousGoodsItem[];
  /** Cargo whose form fields the merge rewrote (see `applyValidationToForm`). */
  changedCargoIds: string[];
  enrichedCount: number;
}

/** Pure merge pipeline used by both in-form commit and hydration. */
export function processRehydration(
  response: SubstanceVerificationResponse,
  formData: ValidationFormData,
  existingResults: ValidationResult[]
): ProcessRehydrationResult {
  const enrichedResults = convertValidationResults(
    response.results ?? [],
    response.enrichedSubstances
  );
  const mergedResults = mergeValidationResults(
    existingResults,
    enrichedResults,
    formData.dangerousGoods
  );
  const stats = applyValidationToForm(formData.dangerousGoods, mergedResults);
  return { mergedResults, enrichedCount: enrichedResults.length, ...stats };
}
